import { z } from 'zod'
import { oc } from '@orpc/contract'

export const scribeRecordDurationResponseSchema = z.object({
  /** Stored `Encounter.length` in minutes, or `null` when the write was lost. */
  lengthMinutes: z.number().int().min(0).nullable(),
})
export type ScribeRecordDurationResponse = z.infer<typeof scribeRecordDurationResponseSchema>

/**
 * Every segment id sent over HTTP carries this prefix, so it can never collide
 * with the scribe caller's ids (bare session ids, which the browser holds).
 * Mirrored in packages/web/src/model/middleware-types.ts.
 */
export const RECORD_DURATION_SEGMENT_PREFIX = 'zoom:'

/**
 * How far `endedAt` may sit from `clientNow`. The leave edge reports a window
 * ending as it sends (the two are equal), so a wider gap is not a leave-edge
 * report, and shifting it would move an arbitrary window onto the server clock.
 */
const MAX_END_TO_SEND_MS = 5 * 60_000

/**
 * PD-1671 — the only way to write `Encounter.length`. Semantics live in
 * `docs/ENCOUNTER_DURATION.md`.
 */
export const scribeRecordDuration = oc
  .route({ method: 'POST', path: '/v2/scribe/sessions/{encounterId}/duration' })
  .input(
    z
      .object({
        encounterId: z.string(),
        /**
         * Opaque per-observation id minted by the caller; replaying it is a
         * no-op. Omitted together with `startedAt` when there is nothing to
         * measure — a leave with no Zoom join still has to close `period.end`.
         */
        segmentId: z
          .string()
          .startsWith(RECORD_DURATION_SEGMENT_PREFIX)
          .min(RECORD_DURATION_SEGMENT_PREFIX.length + 1)
          .max(64)
          .optional(),
        startedAt: z.string().datetime().optional(),
        /** End of the observation, and the instant `period.end` is closed with. */
        endedAt: z.string().datetime(),
        /**
         * The caller's clock at send. The window is shifted by (server now −
         * clientNow) so it lands on the server clock the scribe caller uses.
         * Required with a segment; optional for a period-only leave.
         */
        clientNow: z.string().datetime().optional(),
        /** Leave edge only — closes `period.end` in the same write, so callers don't race. */
        stampPeriodEnd: z.boolean().optional(),
      })
      // A segment is an id AND a start. One without the other used to be read as
      // "nothing to measure" and dropped with a 200; reject it at the boundary.
      // Range sanity (future, overlong) is the recorder's job: it marks the
      // measurement as lost rather than rejecting the leave edge outright.
      .refine((v) => (v.segmentId === undefined) === (v.startedAt === undefined), {
        message: 'segmentId and startedAt must be sent together, or both omitted',
      })
      .refine(
        (v) =>
          v.segmentId === undefined ||
          (v.clientNow !== undefined &&
            Math.abs(Date.parse(v.endedAt) - Date.parse(v.clientNow)) <= MAX_END_TO_SEND_MS),
        {
          message: 'a segment must be sent at its end: clientNow required, within 5 min of endedAt',
        },
      ),
  )
  .output(scribeRecordDurationResponseSchema)
